class WebRTCService {
  constructor() {
    this.peerConnection = null
    this.localStream = null
    this.remoteAudio = null
  }

  // Check if microphone permission is granted
  static async checkMicrophonePermission() {
    try {
      if (!navigator.permissions) {
        // Permissions API not supported, try to get media directly
        return { state: "prompt" }
      }
      
      const result = await navigator.permissions.query({ name: "microphone" })
      return result
    } catch (error) {
      console.warn("Could not check microphone permission:", error)
      return { state: "prompt" }
    }
  }

  // Request microphone permission from user
  static async requestMicrophonePermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } 
      })
      
      // Stop the tracks immediately as we just wanted to request permission
      stream.getTracks().forEach(track => track.stop())
      
      return { granted: true }
    } catch (error) {
      console.error("Microphone permission denied:", error)
      
      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        return { granted: false, error: "Permission denied. Please allow microphone access to make calls." }
      } else if (error.name === "NotFoundError") {
        return { granted: false, error: "No microphone found. Please connect a microphone to make calls." }
      } else {
        return { granted: false, error: "Could not access microphone. Please check your device settings." }
      }
    }
  }

  async initialize(iceServers = [{ urls: "stun:stun.relay.metered.ca:80" }]) {
    try {
      // create peer connection
      this.peerConnection = new RTCPeerConnection({
        iceServers,
      })

      // set up event handlers
      this.setupEventHandlers()

      // Get user media (microphone)
      this.localStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } 
      })

      // add local tracks to peer connection
      this.localStream.getTracks().forEach(track => {
        this.peerConnection.addTrack(track, this.localStream)
      })

      return true
    } catch (error) {
      console.error("Error initializing WebRTC:", error)
      throw error
    }
  }

  setupEventHandlers() {
    // handle incoming remote tracks
    this.peerConnection.ontrack = (event) => {
      if (event.streams && event.streams[0]) {
        this.playRemoteAudio(event.streams[0])
      } else if (event.track) {
        // Fallback: create a MediaStream from the track
        const stream = new MediaStream([event.track])
        this.playRemoteAudio(stream)
      }
    }

    // handle ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        // You might want to send this to the server if using trickle ICE
      }
    }

    // handle connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      // When connected, double-check for remote tracks
      if (this.peerConnection.connectionState === "connected") {
        this.checkAndPlayRemoteTracks()
      }
    }

    // handle ICE connection state changes
    this.peerConnection.oniceconnectionstatechange = () => {
      // When ICE is connected, check for remote tracks
      if (this.peerConnection.iceConnectionState === "connected") {
        this.checkAndPlayRemoteTracks()
      }
    }
  }

  checkAndPlayRemoteTracks() {
    const receivers = this.peerConnection.getReceivers()
    
    receivers.forEach((receiver) => {
      const track = receiver.track
      
      // If we have a remote audio track but no audio element, play it
      if (track && track.kind === "audio" && track.readyState === "live" && !this.remoteAudio) {
        const stream = new MediaStream([track])
        this.playRemoteAudio(stream)
      }
    })
  }

  playRemoteAudio(stream) {
    // remove existing element
    if (this.remoteAudio) {
      this.remoteAudio.srcObject = null
      this.remoteAudio.remove()
    }

    // audio element and stream
    this.remoteAudio = document.createElement("audio")
    this.remoteAudio.srcObject = stream
    this.remoteAudio.autoplay = true
    this.remoteAudio.volume = 1.0  // Set volume to max
    this.remoteAudio.muted = false  // Ensure not muted
    this.remoteAudio.style.display = "none"
    this.remoteAudio.id = "remote-audio-element"
    document.body.appendChild(this.remoteAudio)

    this.remoteAudio.onerror = (error) => {
      console.error("Audio element error:", error)
    }

    // play
    this.remoteAudio.play()
      .then(() => {})
      .catch(error => {
        console.error("Error playing remote audio:", error)
      })
  }

  async createAnswer(offerSdp) {
    try {
      // set remote description (the offer from the caller)
      await this.peerConnection.setRemoteDescription({
        type: "offer",
        sdp: offerSdp,
      })

      // create sdp answer
      const answer = await this.peerConnection.createAnswer()

      // set local description
      await this.peerConnection.setLocalDescription(answer)

      // wait for ICE gathering to complete
      await this.waitForICEGathering()

      return this.peerConnection.localDescription.sdp
    } catch (error) {
      console.error("Error creating answer:", error)
      throw error
    }
  }

  async createOffer() {
    try {
      // create offer
      const offer = await this.peerConnection.createOffer()

      // set local description
      await this.peerConnection.setLocalDescription(offer)

      // wait for ICE gathering to complete
      await this.waitForICEGathering()

      return this.peerConnection.localDescription.sdp
    } catch (error) {
      console.error("Error creating offer:", error)
      throw error
    }
  }

  waitForICEGathering() {
    return new Promise((resolve) => {
      if (this.peerConnection.iceGatheringState === "complete") {
        resolve()
      } else {
        const checkState = () => {
          if (this.peerConnection.iceGatheringState === "complete") {
            this.peerConnection.removeEventListener("icegatheringstatechange", checkState)
            resolve()
          }
        }
        this.peerConnection.addEventListener("icegatheringstatechange", checkState)
      }
    })
  }

  cleanup() {
    // stop local stream
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop())
      this.localStream = null
    }

    // remove remote audio element
    if (this.remoteAudio) {
      this.remoteAudio.srcObject = null
      this.remoteAudio.remove()
      this.remoteAudio = null
    }

    // close peer connection
    if (this.peerConnection) {
      this.peerConnection.close()
      this.peerConnection = null
    }
  }

  getConnectionState() {
    return this.peerConnection?.connectionState || "closed"
  }

  getICEConnectionState() {
    return this.peerConnection?.iceConnectionState || "closed"
  }
}

export default WebRTCService
